import { useEffect, useState, useRef, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Link,
  Chip,
  Tooltip,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FileStatusIndicator } from './FileStatusIndicator';
import EditPlans from './EditPlans';
import ViewPlanInfo from './ViewPlanInfo';
import { WithPermission } from '../../Auth/WithPermission';

export const BuildersAndProjectsExpandable = ({ builderId, builders, setBuilders, expandedProjects, onProjectToggle, expandedBuilders, onBuilderToggle,
  searchTerm = '', handleArchivePlan, addNewPlanToBuildersList, highlightPlanId, setHighlightPlanId, filters, setSelectedBuilderId,
  hasAnyFilters, planMatchesFilters }) => {

  //////////////// highlighting of plan when adding or editing
  const planRowRefs = useRef({});

  const findPlanLocation = (id) => {
    for (const builder of groupedBuilders) {
      if (!builder.projects) continue;
      for (const project of builder.projects) {
        if (!project.plans) continue;
        for (const plan of project.plans) {
          if (plan.id === id) {
            return { builderId: builder.id, projectId: project.id, plan };
          }
        }
      }
    }
    return null;
  };

  const pendingScrollRef = useRef(null); // { planId, builderId }
  const highlightingRef = useRef(false);

  useEffect(() => {
    if (!highlightPlanId) return;
    if (highlightingRef.current) return;

    const loc = findPlanLocation(highlightPlanId);
    if (!loc) {
      setHighlightPlanId(null);
      return;
    }

    highlightingRef.current = true;
    setSelectedBuilderId(loc.builderId)

    // remember what we are waiting for
    pendingScrollRef.current = {
      planId: highlightPlanId,
      builderId: loc.builderId,
      projectId: loc.projectId
    };

    // trigger expansion
    onBuilderToggle(loc.builderId, true);
    onProjectToggle(loc.projectId, true);
  }, [highlightPlanId]);

  useEffect(() => {
    setTimeout(() => {

      const pending = pendingScrollRef.current;
      if (!pending) return;

      const { planId, builderId, projectId } = pending;

      // builder not expanded yet → wait
      if (!expandedBuilders[builderId]) return;

      // project not expanded yet → wait
      if (!expandedProjects[projectId]) return;

      // builder is expanded → now the row can exist
      const el = planRowRefs.current[planId];
      if (!el) return;

      pendingScrollRef.current = null;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch {
            el.scrollIntoView();
          }

          const originalBg = window.getComputedStyle(el).backgroundColor || 'transparent';

          el.style.backgroundColor = '#FFF8E1';

          const vibranceDuration = 3500;
          const fadeDuration = 2000;

          setTimeout(() => {
            el.style.transition = `background-color ${fadeDuration}ms ease`;
            el.style.backgroundColor = originalBg;
          }, vibranceDuration);

          setTimeout(() => {
            el.style.transition = '';
            el.style.backgroundColor = '';
            highlightingRef.current = false;
            setHighlightPlanId(null);
          }, vibranceDuration + fadeDuration + 50);
        });
      });

    }, 1000)
  }, [expandedBuilders, expandedProjects]);
  //////////////// highlighting of plan when adding or editing

  const [viewingPlan, setViewingPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);

  const handleExpandBuilder = (builderId) => (event, isExpanded) => {
    onBuilderToggle(builderId, isExpanded);
  };

  const handleExpandProject = (projectId) => (event, isExpanded) => {
    onProjectToggle(projectId, isExpanded);
  };

  // group plans by project within each builder
  const groupedBuilders = builders.map(builder => {
    // check if this builder has any empty plans
    const allProjectArchive = builder.plans.every(plan => plan.isArchivedProject);

    if (allProjectArchive) {
      return {
        ...builder,
        isEmpty: true,
        projects: [{
          id: `${builder.id}-empty`,
          name: "No Project Assigned",
          plans: builder.plans,
          isEmpty: true
        }]
      };
    }

    const hasEmptyPlans = builder.plans.some(plan => plan.isEmpty);

    if (hasEmptyPlans) {
      return {
        ...builder,
        isEmpty: true,
        projects: [{
          id: `${builder.id}-empty`,
          name: "No Project Assigned",
          plans: builder.plans,
          isEmpty: true
        }]
      };
    }

    // regular builders - group by project as before
    const projectsMap = builder.plans.reduce((acc, plan) => {
      const projectKey = plan.project || ''; // keep empty string if blank
      if (!acc[projectKey]) {
        acc[projectKey] = {
          id: `${builder.id}-${projectKey}`,
          name: projectKey || "No Project Assigned",
          plans: [],
          isArchivedProject: plan.isArchivedProject || false
        };
      }
      acc[projectKey].plans.push(plan);
      return acc;
    }, {});

    // convert to array and sort using projectSort
    const projectsArray = Object.values(projectsMap);
    projectsArray.sort(projectSort);

    return {
      ...builder,
      projects: projectsArray
    };
  });

  const [expandedRows, setExpandedRows] = useState(new Set());

  const formatDateTooltip = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTable = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const ITEM_HEIGHT = 48;
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenuRow, setActiveMenuRow] = useState(null); // Track which row's menu is open
  const handleMenuClick = (event, rowId) => {
    // Open menu for this row
    setAnchorEl(event.currentTarget);
    setActiveMenuRow(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenuRow(null);
  };

  // Helper function
  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  const HighlightText = ({ text, searchTerm, wordBoundary = false }) => {
    if (!text) return null;
    if (!searchTerm) return <>{text}</>;

    // accept either a string ("foo bar") or an array (["foo","bar"])
    const terms = Array.isArray(searchTerm)
      ? searchTerm.map(t => String(t).trim()).filter(Boolean)
      : String(searchTerm)
        .split(/\s+/)           // split on whitespace
        .map(t => t.trim())
        .filter(Boolean);

    if (terms.length === 0) return <>{text}</>;

    // build one regex that matches any of the terms
    const pattern = terms.map(t => escapeRegExp(t)).join('|');
    const regex = new RegExp(wordBoundary ? `\\b(${pattern})\\b` : `(${pattern})`, 'gi');

    const parts = String(text).split(regex);

    return (
      <>
        {parts.map((part, i) => {
          if (!part) return null;
          // regex split keeps the matched chunks too; check by testing against regex
          if (regex.test(part)) {
            // reset lastIndex because we reused the same regex for test()
            regex.lastIndex = 0;
            return <strong key={i}>{part}</strong>;
          }
          return <span key={i}>{part}</span>;
        })}
      </>
    );
  };

  function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function daysBetween(a, b) {
    const ms = 1000 * 60 * 60 * 24;
    return Math.floor((startOfDay(a) - startOfDay(b)) / ms);
  }

  function isWithinLastDays(dateStr, days) {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = daysBetween(now, date);
    return diffDays >= 0 && diffDays < days;
  }

  function isWithinLastDaysEndingYesterday(dateStr, days) {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    if (isNaN(date)) return false;

    const todayStart = startOfDay(new Date());
    const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
    const earliestStart = new Date(yesterdayStart.getTime() - (days - 1) * 24 * 60 * 60 * 1000);

    const dateStart = startOfDay(date);
    return dateStart >= earliestStart && dateStart <= yesterdayStart;
  }

  const filteredBuilders = groupedBuilders.filter(
    (builder) =>
      !builder.isArchivedBuilder && builder.id === builderId
  );

  // create seperate list of builders with projects where each project's plans are filtered
  const buildersFilteredByPlanFilters = useMemo(() => {
    return groupedBuilders.map(builder => {
      // filter plans inside each project using the same planMatchesFilters
      const projects = (builder.projects || []).map(project => ({
        ...project,
        plans: (project.plans || []).filter(plan => planMatchesFilters(plan, filters))
      }));

      const visibleProjects = hasAnyFilters
        ? projects.filter(p => !p.isArchivedProject && (p.plans?.length || 0) > 0)
        : projects.filter(p => !p.isArchivedProject);

      visibleProjects.sort(projectSort);

      if (!hasAnyFilters && builder.isEmpty && (visibleProjects.length === 0)) {
        return { ...builder, projects: builder.projects };
      }

      return { ...builder, projects: visibleProjects };
    });
  }, [groupedBuilders, filters, hasAnyFilters]);

  const displayedBuilders = buildersFilteredByPlanFilters.filter(
    (builder) => !builder.isArchivedBuilder && builder.id === builderId
  );

  // calculate hasAnyPlansWhenFiltered from displayedBuilders
  const hasAnyPlansWhenFiltered = useMemo(() => {
    return displayedBuilders.reduce((bAcc, b) => {
      const projectPlanCount = (b.projects || []).reduce((pAcc, p) => pAcc + ((p.plans && p.plans.length) || 0), 0);
      return bAcc + projectPlanCount;
    }, 0);
  }, [displayedBuilders]);

  function projectSort(a, b) {
    const NO = "No Project Assigned";
    const nameA = (a?.name || "").trim();
    const nameB = (b?.name || "").trim();

    // 'No Project Assigned' always first
    if (nameA === NO && nameB === NO) return 0;
    if (nameA === NO) return -1;
    if (nameB === NO) return 1;

    // if name doesn't start with a number capture leading numeric part (integers or decimals)
    const numA = nameA.match(/^(\d+(\.\d+)?)/);
    const numB = nameB.match(/^(\d+(\.\d+)?)/);

    // both numeric: compare numeric prefixes first, then rest of string
    if (numA && numB) {
      const nA = parseFloat(numA[1]);
      const nB = parseFloat(numB[1]);
      if (nA !== nB) return nA - nB;

      // same numeric prefix: compare remainder case-insensitively
      const restA = nameA.slice(numA[1].length).trim();
      const restB = nameB.slice(numB[1].length).trim();
      return restA.localeCompare(restB, undefined, { sensitivity: 'base' });
    }

    // only A starts with number: A comes first
    if (numA) return -1;
    if (numB) return 1;

    // none numeric: normal case-insensitive alphabetical
    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
  }

  return (
    <Box sx={{ width: '100%' }}>
      {displayedBuilders.length > 0 ? (

        // if has any plans or no filters applied
        hasAnyPlansWhenFiltered > 0 || !hasAnyFilters ? (

          displayedBuilders.map((builder) => {
            return (builder.projects && builder.projects.length > 0) ?
              (
                // render projects (including the generated "No Project Assigned" project)  
                builder.projects.filter(project => !project.isArchivedProject).map((project) => (
                  <Accordion
                    key={project.id}
                    expanded={expandedProjects[project.id] || searchTerm || false}
                    onChange={handleExpandProject(project.id)}
                    sx={{
                      mb: 2,
                      // ensure summary background covers table when sticky
                      '& .MuiAccordionSummary-root': {
                        backgroundColor: 'background.paper',
                      },
                      // When this accordion is expanded, make its summary sticky
                      '&.Mui-expanded .MuiAccordionSummary-root': {
                        position: 'sticky',
                        top: 'calc(var(--app-header-height) + var(--app-plans-menu-height))',
                        zIndex: 20, // above the table rows
                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                      }
                    }}
                    slotProps={{
                      transition: {
                        timeout: 150,
                      },
                    }}
                  >
                    <AccordionSummary
                      data-testid={`project-summary-${project.id}`}
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        flexDirection: 'row-reverse',
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          transform: 'rotate(-90deg)',
                        },
                        '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                          transform: 'rotate(0deg)',
                        }
                      }}
                    >
                      <Typography variant="h6">
                        <HighlightText variant="subtitle1" text={project.name} searchTerm={searchTerm} />
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        p: 1,
                        overflow: 'visible'
                      }}
                    >
                      <TableContainer component={Paper}
                        sx={{
                          boxShadow: 'none',
                          border: 'none',
                          overflow: 'visible'
                        }}>
                        <Table
                          sx={{
                            // Ensure table doesn't create positioning context
                            position: 'relative'
                          }}
                        >
                          <TableHead
                            sx={{
                              marginTop: "5px",
                              position: 'sticky',
                              top: 'calc(var(--app-header-height) + var(--app-plans-menu-height) + 72px)',
                              zIndex: 19,
                              backgroundColor: 'background.paper'
                            }}
                          >
                            <TableRow
                              sx={{
                                position: 'relative',
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  height: '1px',
                                  backgroundColor: 'lightgrey',
                                  zIndex: 1,
                                }
                              }}
                            >
                              <TableCell>Lot</TableCell>
                              <TableCell>Plan #</TableCell>
                              <TableCell>Elev.</TableCell>
                              <TableCell>Layout</TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>Options</TableCell>
                              <TableCell>Build Stage</TableCell>
                              <TableCell>Modified</TableCell>
                              <TableCell>File Date</TableCell>
                              <TableCell>File Name</TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {project.plans.length === 1 && project.plans[0].isEmptyProject ? (
                              <TableRow>
                                {/* Empty project state */}
                              </TableRow>
                            ) : (
                              project.plans
                                .filter(plan => !plan.archived && !plan.isPlaceholder)
                                .filter(plan => {
                                  if (!filters.notReady) return true;
                                  return plan.fileStatus === "processing" || plan.fileStatus === "error";
                                })
                                .filter(plan => {
                                  const anyDateSelected = filters.today || filters.yesterday || filters.last7;
                                  if (!anyDateSelected) return true;
                                  const modifiedAt = plan.planModifiedDateTime;
                                  const matchesToday = filters.today && isWithinLastDays(modifiedAt, 1);
                                  const matchesYesterday =
                                    filters.yesterday &&
                                    isWithinLastDays(modifiedAt, 2) &&
                                    !isWithinLastDays(modifiedAt, 1); // yesterday only, not today
                                  const matchesLast7 = filters.last7 && isWithinLastDaysEndingYesterday(modifiedAt, 7);
                                  return matchesToday || matchesYesterday || matchesLast7;
                                })
                                .map((plan) => (
                                  <TableRow
                                    key={plan.id}
                                    hover
                                    ref={(el) => {
                                      if (el) {
                                        planRowRefs.current[plan.id] = el;
                                      } else {
                                        // if unmounted, delete ref
                                        delete planRowRefs.current[plan.id];
                                      }
                                    }}
                                  >
                                    {/* Your plan row content */}
                                    <TableCell>
                                      <HighlightText text={plan.lot} searchTerm={searchTerm} />
                                    </TableCell>
                                    <TableCell>
                                      <HighlightText text={plan.planNumber} searchTerm={searchTerm} />
                                    </TableCell>
                                    <TableCell>
                                      <HighlightText text={plan.elevation} searchTerm={searchTerm} />
                                    </TableCell>
                                    <TableCell>{plan.layout}</TableCell>
                                    <TableCell sx={{ textAlign: 'center', maxWidth: "120px" }}>
                                      {plan.options.length === 0 ? null : searchTerm || expandedRows.has(plan.id) ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                          {plan.options.map((option, index) => (
                                            <Tooltip key={index} title={option.planOptionName} arrow>
                                              <Link
                                                sx={{ cursor: "pointer" }}
                                                onClick={() => {
                                                  const newSet = new Set(expandedRows);
                                                  newSet.delete(plan.id);
                                                  setExpandedRows(newSet);
                                                }}
                                              >
                                                <Chip
                                                  label={
                                                    <Box sx={{
                                                      maxWidth: '120px',
                                                      whiteSpace: 'nowrap',
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      display: 'inline-block'
                                                    }}>
                                                      <HighlightText
                                                        text={option.planOptionName}
                                                        searchTerm={searchTerm}
                                                      />
                                                    </Box>
                                                  }
                                                  sx={{
                                                    margin: '2px',
                                                    '& .MuiChip-label': {
                                                      display: 'block',
                                                      overflow: 'hidden'
                                                    }
                                                  }}
                                                  size="small"
                                                />
                                              </Link>
                                            </Tooltip>
                                          ))}
                                        </div>
                                      ) : (
                                        <Link
                                          sx={{ cursor: "pointer" }}
                                          onClick={() => {
                                            const newSet = new Set(expandedRows);
                                            newSet.add(plan.id);
                                            setExpandedRows(newSet);
                                          }}
                                        >
                                          <Chip label={plan.options.length} size="small" />
                                        </Link>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <HighlightText text={plan.buildStages[0]?.buildStageName} searchTerm={searchTerm} />
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip
                                        title={
                                          `Plan last modified on ${formatDateTooltip(plan.planModifiedDateTime)}.`
                                        }
                                        arrow
                                      >
                                        <span>{plan?.planModifiedDateTime ? formatDateTable(plan.planModifiedDateTime) : ""}</span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip
                                        title={
                                          `File last modified on ${formatDateTooltip(plan.lastModifiedCadFileDateTime)}. Uploaded on ${formatDateTooltip(plan.uploadedCadFileDateTime)} by ${plan.uploadedCadFileBy || 'unknown'}`
                                        }
                                        arrow
                                      >
                                        <span>{formatDateTable(plan.lastModifiedCadFileDateTime)}</span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      <FileStatusIndicator
                                        fileName={
                                          <HighlightText text={plan.fileName} searchTerm={searchTerm} />
                                        }
                                        status={plan.fileStatus}
                                        searchTerm={searchTerm}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <div>
                                        <IconButton
                                          aria-label="more"
                                          onClick={(e) => handleMenuClick(e, plan.id)}
                                        >
                                          <MoreVertIcon />
                                        </IconButton>

                                        <Menu
                                          anchorEl={anchorEl}
                                          open={Boolean(anchorEl) && activeMenuRow === plan.id}
                                          onClose={handleMenuClose}
                                          slotProps={{
                                            paper: {
                                              sx: {
                                                maxHeight: ITEM_HEIGHT * 4.5,
                                                width: '20ch',
                                              },
                                            },
                                          }}
                                        >
                                          <MenuItem onClick={() => {
                                            const selectedPlan = project.plans.find(p => p.id === activeMenuRow);
                                            selectedPlan.builderName = builder.name
                                            setViewingPlan(selectedPlan);
                                            handleMenuClose();
                                          }}>
                                            View plan info
                                          </MenuItem>

                                          <WithPermission rolesRequired={['admin', 'superadmin', 'useredit']}>
                                            <MenuItem
                                              onClick={() => {
                                                const selectedPlan = project.plans.find(p => p.id === activeMenuRow);
                                                if (selectedPlan) {
                                                  selectedPlan.builderId = builder.id;
                                                  selectedPlan.builderName = builder.name;
                                                  selectedPlan.project = project.name;

                                                  setEditingPlan(selectedPlan);
                                                }
                                                handleMenuClose();
                                              }}
                                            >
                                              Edit plan info
                                            </MenuItem>

                                            <MenuItem
                                              onClick={() => {
                                                handleArchivePlan(activeMenuRow)
                                                handleMenuClose()
                                              }}
                                              sx={{ color: "red" }}>
                                              Archive plan
                                            </MenuItem>
                                          </WithPermission>
                                        </Menu>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (

                // Empty builder (no projects)
                <Box key={builder.id}>
                  {/* Empty builder content */}
                </Box>

              );
          })

        ) : (
          <Box>
            <Typography variant="h4" sx={{ textAlign: "center" }}>
              No matching results here
            </Typography>
          </Box>
        )

      ) : searchTerm.length > 0 ? (
        <Box>
          <Typography variant="h4" sx={{ textAlign: "center" }}>
            No matching results here
          </Typography>
        </Box>
      ) : null}

      {editingPlan && (
        <EditPlans
          key={editingPlan.id}
          plan={editingPlan}
          onClose={() => setEditingPlan(null)}
          setBuildersList={setBuilders}
          addNewPlanToBuildersList={addNewPlanToBuildersList}
          buildersList={builders}
          setHighlightPlanId={setHighlightPlanId}
        />
      )}


      {viewingPlan && (
        <ViewPlanInfo
          plan={viewingPlan}
          onClose={() => setViewingPlan(null)}
        />
      )}
    </Box>
  );
};